import React, { useState, useEffect, useCallback } from "react";
import {
  Search, RefreshCw, X, Briefcase, ChevronDown, ChevronLeft,
  ChevronRight, Filter, Eye, MapPin, Building2, FileText,
  CheckCircle, Wifi, ChevronUp,
  Users, ArrowUpDown, RotateCcw, BarChart3,
  Clock, Target, AlertCircle, CircleDot, Activity,
  Download, CheckCheck,
  Calendar, User, Phone, Mail, Layers, Video,
  MonitorPlay, ClipboardList, MoreVertical, DollarSign,
  UserX, Send, ThumbsDown,
} from "lucide-react";

/* ─── helpers ─── */
const norm = (v, fb = "-") => { const s = String(v ?? "").trim(); return s || fb; };
const normOpt = (v) => { const s = String(v ?? "").trim(); return ["null","undefined",""].includes(s.toLowerCase()) ? "" : s; };

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const stageConfig = {
  "Open":     { color: "#10b981", bg: "#ecfdf5", border: "#a7f3d0", label: "Open",     icon: CircleDot  },
  "Re-Open":  { color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", label: "Re-Open",  icon: RotateCcw  },
  "Active":   { color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", label: "Active",   icon: Activity   },
  "default":  { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", label: "Unknown",  icon: AlertCircle },
};
const getStage = (s) => stageConfig[s] || stageConfig["default"];

/* ─── Interview Status color ─── */
const interviewStatusColor = (s = "") => {
  const lc = s.toLowerCase();
  if (lc.includes("schedul"))  return { bg:"#f0fdf4", color:"#166534", border:"#bbf7d0" };
  if (lc.includes("complet"))  return { bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe" };
  if (lc.includes("cancel"))   return { bg:"#fef2f2", color:"#b91c1c", border:"#fecaca" };
  if (lc.includes("pending"))  return { bg:"#fffbeb", color:"#92400e", border:"#fde68a" };
  if (lc.includes("select"))   return { bg:"#f5f3ff", color:"#5b21b6", border:"#ddd6fe" };
  if (lc.includes("reject"))   return { bg:"#fef2f2", color:"#b91c1c", border:"#fecaca" };
  return { bg:"#f8fafc", color:"#475569", border:"#e2e8f0" };
};

/* ─── Submission Status color ─── */
const submissionStatusColor = (s = "") => {
  const status = String(s).toLowerCase().trim();
  if (status === "submitted" || status.includes("submit"))
    return { bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe", label: s };
  if (status === "interview scheduled" || status.includes("interview schedul"))
    return { bg:"#f5f3ff", color:"#6d28d9", border:"#ddd6fe", label: s };
  if (status === "placed" || status.includes("place"))
    return { bg:"#ecfdf5", color:"#047857", border:"#a7f3d0", label: s };
  if (status === "offer extended" || status.includes("offer"))
    return { bg:"#dcfce7", color:"#166534", border:"#86efac", label: s };
  if (status === "rejected" || status.includes("reject"))
    return { bg:"#fef2f2", color:"#b91c1c", border:"#fecaca", label: s };
  if (status === "on hold" || status.includes("hold"))
    return { bg:"#fef3c7", color:"#92400e", border:"#fcd34d", label: s };
  if (status === "withdrawn" || status.includes("withdraw"))
    return { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label: s };
  if (status === "client review" || status.includes("review"))
    return { bg:"#e0e7ff", color:"#3730a3", border:"#a5b4fc", label: s };
  return { bg:"#e0f2fe", color:"#075985", border:"#7dd3fc", label: s };
};


function FormField({ label, name, type = 'text', required = false, placeholder, options, readOnly = false, formData, errors, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {options ? (
        <select
          name={name}
          value={formData[name]}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors[name] ? 'border-red-300 bg-red-50' : 'border-slate-200'
          }`}
        >
          <option value="">Select...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors[name] ? 'border-red-300 bg-red-50' : 'border-slate-200'
          } ${readOnly ? 'bg-slate-100 cursor-not-allowed' : ''}`}
        />
      )}
      {errors[name] && (
        <p className="text-xs text-red-500 mt-1">{errors[name]}</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   NEGOTIATION FORM MODAL
═══════════════════════════════════ */
function NegotiationFormModal({ candidate, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    candidateName: '',
    phoneNumber: '',
    emailId: '',
    candidateLocation: '',
    jobTitle: '',
    coreSkills: '',
    clientName: '',
    clientTrack: '',
    endClient: '',
    interviewStatus: '',
    clientRate: '',
    originalCandidateRate: '',
    negotiatedRate: '',
    reducedRate: '',
    leadersHandling: '',
    additionalComments: '',
    startDate: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (candidate) {
      setFormData(prev => ({
        ...prev,
        date: candidate.date || new Date().toISOString().split('T')[0],
        job_id: candidate.job_id || '',
        submission_id: candidate.submission_id || '',
        applicant_id: candidate.applicant_id || '',
        interview_id: candidate.interview_id || '',
        candidateName: norm(candidate.candidateName || candidate.name || candidate.candidate_name, ''),
        phoneNumber: norm(candidate.candidatePhone || candidate.phone || candidate.mobile_number || candidate.phoneNumber, ''),
        emailId: norm(candidate.candidateEmail || candidate.email || candidate.email_id || candidate.emailId, ''),
        candidateLocation: candidate.candidateLocation || candidate.location || '',
        jobTitle: norm(candidate.jobTitle || candidate.job_title, ''),
        coreSkills: norm(candidate.coreSkills || candidate.primarySkills || candidate.skills || candidate.core_skills, ''),
        clientName: norm(candidate.clientName || candidate.client || candidate.client_name, ''),
        clientTrack: norm(candidate.clientTrack || candidate.client_track, ''),
        endClient: norm(candidate.endClient || candidate.end_client, ''),
        interviewStatus: norm(candidate.interviewStatus || candidate.interview_status, ''),
        clientRate: candidate.clientRate || norm(candidate.client_rate, ''),
        originalCandidateRate: candidate.originalCandidateRate || norm(candidate.payRate || candidate.pay_rate || candidate.candidate_rate, ''),
        negotiatedRate: candidate.negotiatedRate || '',
        reducedRate: candidate.reducedRate || '',
        leadersHandling: candidate.leadersHandling || '',
        startDate: candidate.startDate || norm(candidate.start_date, ''),
        additionalComments: candidate.additionalComments || '',
      }));
    }
  }, [candidate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'originalCandidateRate' || name === 'negotiatedRate') {
      const original = name === 'originalCandidateRate' ? parseFloat(value) : parseFloat(formData.originalCandidateRate);
      const negotiated = name === 'negotiatedRate' ? parseFloat(value) : parseFloat(formData.negotiatedRate);
      if (!isNaN(original) && !isNaN(negotiated)) {
        const reduced = (original - negotiated).toFixed(2);
        setFormData(prev => ({ ...prev, reducedRate: reduced }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.candidateName.trim()) newErrors.candidateName = 'Required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Required';
    if (!formData.emailId.trim()) newErrors.emailId = 'Required';
    if (!formData.candidateLocation.trim()) newErrors.candidateLocation = 'Required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Required';
    if (!formData.clientName.trim()) newErrors.clientName = 'Required';
    if (!formData.interviewStatus.trim()) newErrors.interviewStatus = 'Required';
    if (!formData.clientRate.trim()) newErrors.clientRate = 'Required';
    if (!formData.originalCandidateRate.trim()) newErrors.originalCandidateRate = 'Required';
    if (!formData.negotiatedRate.trim()) newErrors.negotiatedRate = 'Required';
    if (!formData.leadersHandling.trim()) newErrors.leadersHandling = 'Required';
    const original = parseFloat(formData.originalCandidateRate);
    const negotiated = parseFloat(formData.negotiatedRate);
    if (!isNaN(original) && !isNaN(negotiated)) {
      const diff = original - negotiated;
      if (diff < 0.50) {
  newErrors.negotiatedRate = 'Reduction must be at least $0.50';
}
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Negotiation form submission error:', error);
      alert('Failed to submit negotiation form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(2,6,23,0.7)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl rounded-2xl overflow-hidden my-8 flex flex-col"
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 32px 80px -16px rgba(0,0,0,0.35)",
          animation: "modalIn 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
          maxHeight: "90vh",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ height: 3, background: "linear-gradient(90deg,#0f172a,#1d4ed8,#7c3aed)" }} />
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg,#0f172a,#1e3a8a)" }}>
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
  <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-0.5">Rate Negotiation Form</p>
  <h3 className="text-lg font-bold text-slate-900 leading-tight">
    {candidate?.candidateName || formData.candidateName || "Candidate"}
  </h3>
  <p className="text-xs text-slate-500 mt-0.5">Complete negotiation details for candidate</p>
</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Date" name="date" type="date" required formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Candidate Name" name="candidateName" required placeholder="Full name" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Phone Number" name="phoneNumber" required placeholder="+1 (555) 123-4567" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Email ID" name="emailId" type="email" required placeholder="candidate@example.com" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Candidate Location" name="candidateLocation" required placeholder="e.g., Austin, TX, USA" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Job Title" name="jobTitle" required placeholder="e.g., Senior Java Developer" formData={formData} errors={errors} onChange={handleChange} />
<div className="md:col-span-2">
  <FormField label="Core Skills" name="coreSkills" required placeholder="e.g., Java, Spring Boot, Microservices" formData={formData} errors={errors} onChange={handleChange} />
</div>
<FormField label="Client Name" name="clientName" required placeholder="Company name" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Client Track" name="clientTrack" placeholder="e.g., Track A, Track B" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="End Client" name="endClient" placeholder="Final client name" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Interview Status" name="interviewStatus" options={['Scheduled', 'Completed', 'Selected', 'Offer Extended', 'Placed', 'Rejected']} required formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Client Rate ($)" name="clientRate" type="number" step="0.01" required placeholder="100.00" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Original Candidate Rate ($)" name="originalCandidateRate" type="number" step="0.01" required placeholder="85.00" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Negotiated Rate ($)" name="negotiatedRate" type="number" step="0.01" required placeholder="84.00" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Reduced Rate ($)" name="reducedRate" type="number" step="0.01" readOnly placeholder="Auto-calculated" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Leaders Handling Rate Negotiation" name="leadersHandling" type="text" required placeholder="e.g., LR, TL1, TL2, DAL, DM, ADD, CAL" formData={formData} errors={errors} onChange={handleChange} />
<FormField label="Start Date" name="startDate" type="date" formData={formData} errors={errors} onChange={handleChange} />
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Additional Comments
              </label>
              <textarea
                name="additionalComments"
                value={formData.additionalComments}
                onChange={handleChange}
                rows={3}
                placeholder="Any additional notes or comments..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-xs text-blue-700">
              <p className="font-semibold">Important:</p>
              <p className="mt-1">Negotiated rate must be at least $0.50 less than the original candidate rate.</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting...</>
              ) : (
                <><CheckCircle className="w-4 h-4" /> Submit Negotiation</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   RD FORWARD MODAL
═══════════════════════════════════ */
function RDForwardModal({ candidate, onClose, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
  setSubmitting(true);
  try {
    await onSubmit(candidate);
    onClose();
  } catch (err) {
    alert(`❌ Error: ${err.message}`); // now shows real error
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.7)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl overflow-hidden"
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 32px 80px -16px rgba(0,0,0,0.35)",
          animation: "modalIn 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Accent bar */}
        <div style={{ height: 3, background: "linear-gradient(90deg,#7f1d1d,#dc2626,#f97316)" }} />

        {/* Body */}
        <div className="p-6 flex flex-col items-center text-center gap-4">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#7f1d1d,#dc2626)" }}>
            <UserX className="w-7 h-7 text-white" />
          </div>

          {/* Title */}
          <div>
            <h3 className="text-base font-bold text-slate-900">Interview Not Cleared</h3>
            <p className="text-xs text-slate-500 mt-1">Forward to RD Team?</p>
          </div>

          {/* Candidate chip */}
          <div className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-left">
            <p className="text-xs text-slate-400 font-medium mb-0.5">Candidate</p>
            <p className="text-sm font-semibold text-slate-800">{candidate?.candidateName || "—"}</p>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{candidate?.jobTitle} · {candidate?.clientName}</p>
          </div>

          {/* Warning note */}
          <div className="w-full p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2 text-left">
            <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
            <p className="text-xs text-orange-700">
              This will mark the interview as <span className="font-semibold">not cleared</span> and forward the candidate record to the RD Team.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex w-full gap-2 mt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg,#dc2626,#ea580c)", boxShadow: "0 4px 14px rgba(220,38,38,0.3)" }}
            >
              {submitting
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Forwarding...</>
                : <><Send className="w-4 h-4" /> Yes, Forward</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   INTERVIEWS INLINE SECTION
═══════════════════════════════════ */
function InterviewsSection({ job, currentUser, onLoadingChange }) {
  const [loading, setLoading]       = useState(true);
  const [loadPct, setLoadPct]       = useState(0);
  const [interviews, setInterviews] = useState([]);
  const [error, setError]           = useState(null);
  const [expanded, setExpanded]     = useState(null);

  // Negotiation
  const [showNegotiationForm, setShowNegotiationForm]   = useState(false);
  const [selectedForNegotiation, setSelectedForNegotiation] = useState(null);
  const [loadingNegotiation, setLoadingNegotiation]     = useState(null);

  // RD Forward
  const [showRDForwardModal, setShowRDForwardModal]     = useState(false);
  const [selectedForRDForward, setSelectedForRDForward] = useState(null);
  const [loadingRDForward, setLoadingRDForward]         = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setLoadPct(0);
        onLoadingChange?.(job.id, true);

        let pct = 0;
        const ticker = setInterval(() => {
          pct = Math.min(pct + Math.random() * 18, 88);
          setLoadPct(Math.round(pct));
        }, 220);

        const res = await fetch(
          `https://candi.vdpl.co/api/index.php/api_interview_details/${encodeURIComponent(job?.external_id)}`,
          { method: "GET" }
        );
        clearInterval(ticker);
        setLoadPct(95);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (!(data?.success === 1 || data?.success === true))
          throw new Error(data?.message || "Failed to load interviews");

        const rawInterviews = Array.isArray(data?.interviews) ? data.interviews : [];
        const submissions   = data?.submissions  ?? {};
        const applicants    = data?.applicants   ?? {};

        const merged = rawInterviews.map(iv => {
          const sid = iv?.submission_id ?? null;
          const sub = sid ? (submissions[sid] ?? {}) : {};
          const aid = sub?.applicant_id ?? null;
          const app = aid ? (applicants[aid] ?? {}) : {};
          return {
            interview_details:  iv,
            submission_details: sub,
            applicant_details:  app,
          };
        });

        setInterviews(merged);
        setLoadPct(100);
        await new Promise(r => setTimeout(r, 300));
      } catch (e) {
        setError(e.message || "Failed to load interviews.");
      } finally {
        setLoading(false);
        setLoadPct(0);
        onLoadingChange?.(job.id, false);
      }
    };
    load();
  }, [job?.external_id]);

  const iv  = (item) => item?.interview_details  ?? {};
  const sub = (item) => item?.submission_details ?? {};
  const app = (item) => item?.applicant_details  ?? {};

  const candidateName = (item) => {
    const a = app(item);
    const full = `${a?.firstname ?? ""} ${a?.middlename ? a.middlename + " " : ""}${a?.lastname ?? ""}`.trim();
    return norm(full || sub(item)?.candidate_name, "Unknown Candidate");
  };

  const interviewType   = (item) => norm(iv(item)?.interview_mode || iv(item)?.mode || iv(item)?.interview_type, "—");
  const interviewDate   = (item) => iv(item)?.interview_date || iv(item)?.scheduled_date;
  const interviewStatus = (item) => norm(iv(item)?.status || iv(item)?.interview_status, "Scheduled");
  const interviewEndTime = (item) => norm(iv(item)?.end_time, "");

  const getStageColor = (item) => {
    const round  = String(iv(item)?.interview_round || iv(item)?.round || "").toLowerCase();
    const status = String(iv(item)?.status || iv(item)?.interview_status || "").toLowerCase();
    const mode   = String(iv(item)?.interview_mode || iv(item)?.mode || "").toLowerCase();

    if (round === "1" || mode.includes("l1") || status.includes("l1"))
      return { bg: "#fef3c7", color: "#92400e", border: "#fcd34d", label: "L1 Interview" };
    if (round === "2" || mode.includes("l2") || status.includes("l2"))
      return { bg: "#ddd6fe", color: "#5b21b6", border: "#c4b5fd", label: "L2 Interview" };
    if (round === "3" || mode.includes("l3") || status.includes("l3"))
      return { bg: "#fbcfe8", color: "#9f1239", border: "#f9a8d4", label: "L3 Interview" };
    if (status.includes("schedul"))
      return { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd", label: "Scheduled" };
    if (status.includes("complet"))
      return { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7", label: "Completed" };
    if (status.includes("cancel"))
      return { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", label: "Cancelled" };
    if (status.includes("pending"))
      return { bg: "#fef3c7", color: "#92400e", border: "#fcd34d", label: "Pending" };
    if (status.includes("select") || status.includes("pass"))
      return { bg: "#dcfce7", color: "#166534", border: "#86efac", label: "Selected" };
    if (status.includes("reject") || status.includes("fail"))
      return { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", label: "Rejected" };
    if (mode.includes("technical") || mode.includes("tech"))
      return { bg: "#e0e7ff", color: "#3730a3", border: "#a5b4fc", label: "Technical" };
    if (mode.includes("hr") || mode.includes("cultural"))
      return { bg: "#fed7aa", color: "#9a3412", border: "#fdba74", label: "HR Round" };
    if (mode.includes("final"))
      return { bg: "#fce7f3", color: "#831843", border: "#f9a8d4", label: "Final Round" };
    return { bg: "#f1f5f9", color: "#475569", border: "#cbd5e0", label: "Interview" };
  };

  /* ── Prepare data for Negotiation Form ── */
  const prepareCandidateForNegotiation = async (item) => {
    const a = app(item);
    const s = sub(item);
    const i = iv(item);

    const jobId       = job?.external_id || '';
    const applicantId = a?.id || s?.job_seeker_id || '';
    const interviewId = i?.id || '';

    const buildLocation = () => {
      if (a?.location) return norm(a.location, '');
      if (a?.city || a?.state || a?.country) return [a.city, a.state, a.country].filter(Boolean).join(', ');
      if (s?.location) return norm(s.location, '');
      if (s?.city || s?.state || s?.country) return [s.city, s.state, s.country].filter(Boolean).join(', ');
      if (i?.location) return norm(i.location, '');
      return '';
    };

    const baseData = {
      job_id:        jobId,
      submission_id: s?.id || '',
      applicant_id:  applicantId,
      interview_id:  interviewId,
      candidateName: candidateName(item),
      candidatePhone: norm(a?.mobile_number || a?.phone || s?.phone, ""),
      candidateEmail: norm(a?.email_id || a?.email || s?.email, ""),
      candidateLocation: buildLocation(),
      jobTitle:    norm(job?.job_title, ""),
      coreSkills:  norm(s?.skills || s?.primary_skills, ""),
      clientName:  norm(job?.client, ""),
      clientTrack: norm(job?.client_track, ""),
      endClient:   norm(job?.end_client, ""),
      interviewStatus: interviewStatus(item),
      clientRate:   norm(job?.client_rate, ""),
      originalCandidateRate: norm(s?.pay_rate || s?.rate, ""),
      startDate:    norm(s?.start_date || job?.start_date, ""),
    };

    try {
      const response = await fetch(
        `https://candi.vdpl.co/Candidates/Candidates/api_get_negotiation.php?job_id=${encodeURIComponent(jobId)}&applicant_id=${encodeURIComponent(applicantId)}&interview_id=${encodeURIComponent(interviewId)}`
      );
      if (!response.ok) throw new Error('Failed to fetch negotiation data');
      const result = await response.json();
      if (result.success && result.exists && result.data) {
        const dbData = result.data;
        return {
          ...baseData,
          date:                  dbData.date                   || baseData.date,
          candidateName:         dbData.candidate_name         || baseData.candidateName,
          phoneNumber:           dbData.phone_number           || baseData.candidatePhone,
          emailId:               dbData.email_id               || baseData.candidateEmail,
          candidateLocation:     dbData.candidate_location     || baseData.candidateLocation,
          jobTitle:              dbData.job_title              || baseData.jobTitle,
          coreSkills:            dbData.core_skills            || baseData.coreSkills,
          clientName:            dbData.client_name            || baseData.clientName,
          clientTrack:           dbData.client_track           || baseData.clientTrack,
          endClient:             dbData.end_client             || baseData.endClient,
          interviewStatus:       dbData.interview_status       || baseData.interviewStatus,
          clientRate:            dbData.client_rate            || baseData.clientRate,
          originalCandidateRate: dbData.original_candidate_rate || baseData.originalCandidateRate,
          negotiatedRate:        dbData.negotiated_rate        || '',
          reducedRate:           dbData.reduced_rate           || '',
          leadersHandling:       dbData.leaders_handling       || '',
          additionalComments:    dbData.additional_comments    || '',
          startDate:             dbData.start_date             || baseData.startDate,
          negotiation_id:        dbData.id,
          isExisting:            true,
        };
      }
    } catch (error) {
      console.error('Error fetching negotiation data:', error);
    }
    return baseData;
  };

  /* ── Negotiation Submit ── */
  const handleNegotiationSubmit = async (formData) => {
    try {
      const response = await fetch('https://candi.vdpl.co/Candidates/Candidates/api_negotiation.php', {
        method: formData.isExisting ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          id:            formData.negotiation_id,
          job_id:        formData.job_id        || job?.external_id || job?.job_pk || '',
          submission_id: formData.submission_id || '',
          applicant_id:  formData.applicant_id  || '',
          interview_id:  formData.interview_id  || '',
          job_code:      job?.job_code          || '',
          created_by:    currentUser?.id        || currentUser?.user_id || null,
          updated_by:    currentUser?.id        || currentUser?.user_id || null,
        }),
      });
      if (!response.ok) throw new Error('Failed to submit negotiation');
      const data = await response.json();
      if (data.success) {
        alert(`✅ Negotiation form ${formData.isExisting ? 'updated' : 'submitted'} successfully!`);
        setShowNegotiationForm(false);
        setSelectedForNegotiation(null);
      } else {
        throw new Error(data.message || 'Failed to submit');
      }
    } catch (error) {
      console.error('Negotiation submission error:', error);
      alert(`❌ Failed to submit: ${error.message}`);
      throw error;
    }
  };


  /* ── Step 1: Save to candidates table ── */
const saveToCandiates = async (candidateData) => {
  const payload = {
    candidate_name:  candidateData.candidateName  || '',
    job_title:       candidateData.jobTitle        || '',
    candidate_email: candidateData.candidateEmail  || '',
    candidate_phone: candidateData.candidatePhone  || '',
    client:          candidateData.clientName      || '',
    primary_skills:  candidateData.coreSkills      || '',
    secondary_skills: '',
    city:            candidateData.city            || '',
    state:           candidateData.state           || '',
    country:         candidateData.country         || '',
    created_by:      currentUser?.id || currentUser?.user_id || null,
  };

  const res = await fetch("https://candi.vdpl.co/api/index.php/nego_save_candidate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`nego_save_candidate returned non-JSON (HTTP ${res.status}): ${text.slice(0, 300)}`);
  }

  if (data.status !== 'success') {
    throw new Error(data.message || `nego_save_candidate failed (HTTP ${res.status})`);
  }

  return { candidateId: data.candidate_id, alreadyExisted: data.already_existed };
};

/* ── Step 2: Move candidate to rd_candidate ── */
const moveCandidateToRD = async (candidateId) => {
  const formData = new FormData();
  formData.append("candidate_id", candidateId);

  const res = await fetch("https://candi.vdpl.co/api/index.php/move_candidate", {
    method: "POST",
    body: formData,
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`move_candidate returned non-JSON (HTTP ${res.status}): ${text.slice(0, 200)}`);
  }

  if (!res.ok) {
    throw new Error(`move_candidate failed (HTTP ${res.status}): ${data?.message || text}`);
  }

  // "Already exists" is still a success
  if (
    data.status === true ||
    data.status === "success" ||
    data.message?.toLowerCase().includes("already exists")
  ) {
    return data.message || "success";
  }

  throw new Error(data.message || "Failed to move candidate to RD");
};

/* ── RD Forward Submit ── */
const handleRDForwardSubmit = async (candidateData) => {
  if (!candidateData?.candidateName) {
    throw new Error("Missing candidate name");
  }

  // Step 1 — save to candidates + move to rd_candidate
  const { candidateId, alreadyExisted } = await saveToCandiates(candidateData);

  // Step 2 — move (only needed if not already in rd_candidate)
  if (!alreadyExisted) {
    await moveCandidateToRD(candidateId);
  }

  alert(
    alreadyExisted
      ? `Candidate is already in the RD Team list.`
      : `Candidate forwarded to RD Team successfully!`
  );

  setShowRDForwardModal(false);
  setSelectedForRDForward(null);
};

  return (
    <>
      {/* Negotiation Form Modal */}
      {showNegotiationForm && selectedForNegotiation && (
        <NegotiationFormModal
          candidate={selectedForNegotiation}
          onClose={() => { setShowNegotiationForm(false); setSelectedForNegotiation(null); }}
          onSubmit={handleNegotiationSubmit}
        />
      )}

      {/* RD Forward Modal */}
      {showRDForwardModal && selectedForRDForward && (
        <RDForwardModal
          candidate={selectedForRDForward}
          onClose={() => { setShowRDForwardModal(false); setSelectedForRDForward(null); }}
          onSubmit={handleRDForwardSubmit}
        />
      )}

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 mb-3">
          <MonitorPlay className="w-4 h-4 text-purple-600" />
          <h4 className="text-sm font-bold text-slate-900">Interview Pipeline</h4>
          {!loading && !error && (
            <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
              {interviews.length}
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-6 px-2 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-500" />
                  Fetching interview pipeline...
                </span>
                <span className="text-purple-600 tabular-nums">{loadPct}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${loadPct}%`, background: "linear-gradient(90deg,#7c3aed,#2563eb)" }}
                />
              </div>
            </div>
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse"
                  style={{ animationDelay: `${i * 120}ms` }} />
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-8 gap-2">
            <AlertCircle className="w-8 h-8 text-red-400" />
            <p className="text-xs font-medium text-red-500">{error}</p>
          </div>
        ) : interviews.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-2 bg-slate-50 rounded-lg">
            <MonitorPlay className="w-10 h-10 text-slate-300" />
            <p className="text-xs font-semibold text-slate-600">No interviews scheduled</p>
            <p className="text-xs text-slate-400">Interview records will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {interviews.map((item, idx) => {
              const isOpen      = expanded === idx;
              const name        = candidateName(item);
              const status      = interviewStatus(item);
              const sc          = interviewStatusColor(status);
              const stageColor  = getStageColor(item);
              const iDate       = interviewDate(item);
              const iType       = interviewType(item);
              const round       = norm(iv(item)?.interview_round || iv(item)?.round, "—");

              return (
                <div key={idx}
                  className="rounded-lg border transition-all overflow-hidden"
                  style={{
                    borderColor: isOpen ? "#c4b5fd" : "#e2e8f0",
                    boxShadow: isOpen ? "0 0 0 2px rgba(139,92,246,0.08)" : "0 1px 2px rgba(0,0,0,0.05)",
                  }}>

                  {/* Row Header */}
                  <div
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => setExpanded(isOpen ? null : idx)}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white text-xs font-bold"
                      style={{ background: "linear-gradient(135deg,#6d28d9,#1d4ed8)" }}>
                      {name.charAt(0).toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold shrink-0"
                          style={{ background: stageColor.bg, color: stageColor.color, borderColor: stageColor.border }}>
                          {stageColor.label}
                        </span>
                        {sub(item)?.submission_status || sub(item)?.status ? (() => {
                          const subStatusValue = sub(item)?.submission_status || sub(item)?.status;
                          const subColor = submissionStatusColor(subStatusValue);
                          return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold shrink-0"
                              style={{ background: subColor.bg, color: subColor.color, borderColor: subColor.border }}>
                              Sub: {subColor.label}
                            </span>
                          );
                        })() : null}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {iDate && (
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <Calendar className="w-2.5 h-2.5" /> {fmtDateTime(iDate)}
                          </span>
                        )}
                        {iType !== "—" && (
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <Video className="w-2.5 h-2.5" /> {iType}
                          </span>
                        )}
                        {round !== "—" && (
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <Layers className="w-2.5 h-2.5" /> Round {round}
                          </span>
                        )}
                      </div>
                    </div>

                    <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold shrink-0"
                      style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>
                      {status}
                    </span>

                    <div className="w-5 h-5 flex items-center justify-center text-slate-400 shrink-0">
                      {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">

                        {/* Candidate Info */}
                        <div className="space-y-1.5">
                          <p className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mb-1">Candidate</p>
                          {(app(item)?.email_id || app(item)?.email) && (
                            <div className="flex items-start gap-1.5">
                              <Mail className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-[9px] text-slate-400">Email</p>
                                <p className="text-xs font-medium text-slate-700 break-all">{norm(app(item)?.email_id || app(item)?.email, "")}</p>
                              </div>
                            </div>
                          )}
                          {(app(item)?.mobile_number || app(item)?.phone) && (
                            <div className="flex items-start gap-1.5">
                              <Phone className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-[9px] text-slate-400">Phone</p>
                                <p className="text-xs font-medium text-slate-700">{norm(app(item)?.mobile_number || app(item)?.phone, "")}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Interview Info */}
<div className="space-y-1.5">
  <p className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mb-1">Interview</p>
  {(() => {
    const candidateSource = norm(
  app(item)?.source            ||
  app(item)?.candidate_source  ||
  app(item)?.sourced_from      ||
  sub(item)?.source            ||
  sub(item)?.candidate_source  ||
  sub(item)?.referral_source,
  ""
);
return candidateSource ? (
  <div className="flex items-start gap-1.5">
    <Target className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
    <div>
      <p className="text-[9px] text-slate-400">Candidate Source</p>
      <p className="text-xs font-medium text-slate-700">{candidateSource}</p>
    </div>
  </div>
) : null;
  })()}
  {iv(item)?.interviewer && (
    <div className="flex items-start gap-1.5">
      <User className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
      <div>
        <p className="text-[9px] text-slate-400">Interviewer</p>
        <p className="text-xs font-medium text-slate-700">{norm(iv(item)?.interviewer || iv(item)?.panel_member, "")}</p>
      </div>
    </div>
  )}
  {iv(item)?.location && (
    <div className="flex items-start gap-1.5">
      <MapPin className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
      <div>
        <p className="text-[9px] text-slate-400">Location</p>
        <p className="text-xs font-medium text-slate-700 break-all">{norm(iv(item)?.location || iv(item)?.meeting_link, "")}</p>
      </div>
    </div>
  )}
</div>

                        {/* Submission Info */}
                        <div className="space-y-1.5">
                          <p className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mb-1">Submission</p>
                          {sub(item)?.submission_status || sub(item)?.status ? (() => {
                            const subStatusValue = sub(item)?.submission_status || sub(item)?.status;
                            const subColor = submissionStatusColor(subStatusValue);
                            return (
                              <div className="mb-2 p-2 rounded-md border"
                                style={{ background: subColor.bg, borderColor: subColor.border }}>
                                <p className="text-[9px] font-semibold uppercase mb-0.5" style={{ color: subColor.color }}>
                                  Submission Status
                                </p>
                                <p className="text-xs font-bold" style={{ color: subColor.color }}>{subColor.label}</p>
                              </div>
                            );
                          })() : null}
                          {sub(item)?.recruiter_name && (
                            <div className="flex items-start gap-1.5">
                              <User className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-[9px] text-slate-400">Recruiter</p>
                                <p className="text-xs font-medium text-slate-700">{norm(sub(item)?.recruiter_name || sub(item)?.recruiter, "")}</p>
                              </div>
                            </div>
                          )}
                          {sub(item)?.tax_term && (
                            <div className="flex items-start gap-1.5">
                              <ClipboardList className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-[9px] text-slate-400">Tax Term</p>
                                <p className="text-xs font-medium text-slate-700">{norm(sub(item)?.tax_term, "")}</p>
                              </div>
                            </div>
                          )}
                          {sub(item)?.submitted_date && (
                            <div className="flex items-start gap-1.5">
                              <Calendar className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-[9px] text-slate-400">Submitted Date</p>
                                <p className="text-xs font-medium text-slate-700">{fmtDate(sub(item)?.submitted_date)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ── Action Buttons ── */}
                      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row gap-2">

                        {/* Fill Negotiation Form */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            const interviewId = iv(item)?.id;
                            setLoadingNegotiation(interviewId);
                            try {
                              const candidateData = await prepareCandidateForNegotiation(item);
                              setSelectedForNegotiation(candidateData);
                              setShowNegotiationForm(true);
                            } catch (err) {
                              console.error('Error loading negotiation form:', err);
                              alert('Failed to load negotiation data');
                            } finally {
                              setLoadingNegotiation(null);
                            }
                          }}
                          disabled={loadingNegotiation === iv(item)?.id}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:from-emerald-600 hover:to-green-700 shadow-md hover:shadow-lg transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-wait"
                        >
                          {loadingNegotiation === iv(item)?.id ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Loading...</>
                          ) : (
                            <><DollarSign className="w-4 h-4" /> Fill Negotiation Form</>
                          )}
                        </button>

                        {/* Interview Not Cleared / Forward to RD */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const a = app(item);
                            const s = sub(item);
                            const i = iv(item);
                            setSelectedForRDForward({
  job_id:         job?.external_id || '',
  submission_id:  s?.id            || '',
  applicant_id:   a?.id || s?.job_seeker_id || '',
  interview_id:   i?.id            || '',
  candidateName:  candidateName(item),
  candidateEmail: norm(a?.email_id || a?.email || s?.email, ''),
  candidatePhone: norm(a?.mobile_number || a?.phone || s?.phone, ''),
  jobTitle:       norm(job?.job_title, ''),
  clientName:     norm(job?.client, ''),
  interview_round: norm(i?.interview_round || i?.round, ''),
  coreSkills:     norm(s?.skills || s?.primary_skills || job?.skills, ''), // ADD THIS LINE
});
                            setShowRDForwardModal(true);
                          }}
                          disabled={loadingRDForward === iv(item)?.id}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg shadow-md hover:shadow-lg transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-wait"
                          style={{ background: "linear-gradient(135deg,#dc2626,#ea580c)" }}
                        >
                          {loadingRDForward === iv(item)?.id ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Loading...</>
                          ) : (
                            <><UserX className="w-4 h-4" /> Interview Not Cleared / Forward to RD</>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

/* ─── small helpers for expanded rows ─── */
function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">{title}</p>
      {children}
    </div>
  );
}
function InfoRow({ icon: Icon, label, value }) {
  if (!value || value === "-" || value === "—") return null;
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] text-slate-400 font-medium">{label}</p>
        <p className="text-xs font-semibold text-slate-700 break-all">{value}</p>
      </div>
    </div>
  );
}

/* ─── Submissions Modal ─── */
function SubmissionsModal({ job, onClose }) {
  const [loading, setLoading]         = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError]             = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `https://candi.vdpl.co/api/index.php/getSubmissions?job_pk=${encodeURIComponent(job?.external_id)}`,
          { method: "GET" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!(data?.success === 1 || data?.success === true))
          throw new Error(data?.message || "Failed to load submissions");
        setSubmissions(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setError(e.message || "Failed to load submissions.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [job?.job_pk]);

  const subStageColor = (s) =>
    s === "Submitted"             ? { bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe" }
    : s === "Interview Scheduled" ? { bg:"#f5f3ff", color:"#6d28d9", border:"#ddd6fe" }
    : s === "Offer Extended"      ? { bg:"#ecfdf5", color:"#047857", border:"#a7f3d0" }
    : s === "Rejected"            ? { bg:"#fef2f2", color:"#b91c1c", border:"#fecaca" }
    : { bg:"#fffbeb", color:"#92400e", border:"#fde68a" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.6)", backdropFilter: "blur(8px)" }}
      onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden"
        style={{ background:"#ffffff", border:"1px solid #e2e8f0",
          boxShadow:"0 32px 80px -16px rgba(0,0,0,0.3)",
          animation:"modalIn 0.3s cubic-bezier(0.34,1.56,0.64,1) both" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: "linear-gradient(90deg,#0f172a,#1d4ed8,#7c3aed)" }} />
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background:"linear-gradient(135deg,#0f172a,#1e3a8a)" }}>
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">{job?.job_title}</h3>
              <p className="text-xs text-slate-400 mt-0.5 font-medium tracking-wide uppercase">{job?.job_code} · Submissions</p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 max-h-[55vh] overflow-auto">
          {loading ? (
            <div className="space-y-2.5">
              {[0,1,2].map(i => (
                <div key={i} className="h-14 rounded-xl"
                  style={{ background:"#f1f5f9", animation:`shimPulse 1.4s ease ${i*180}ms infinite` }} />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-12 gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <p className="text-sm font-semibold text-red-500">{error}</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="flex flex-col items-center py-14 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                <Users className="w-7 h-7 text-slate-300" />
              </div>
              <p className="font-bold text-slate-700">No submissions yet</p>
              <p className="text-sm text-slate-400">Candidates submitted for this job will appear here.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Candidate","Email","Stage","Submitted","Recruiter"].map(h => (
                    <th key={h} className="text-left text-[11px] font-bold uppercase tracking-widest text-slate-400 pb-3 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((s, i) => {
                  const sc = subStageColor(s.status);
                  return (
                    <tr key={s.id ?? i} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 pr-4 font-semibold text-slate-800 whitespace-nowrap">
                        {norm(s.candidate_name || (s.first_name ? `${s.first_name} ${s.last_name}` : s.name))}
                      </td>
                      <td className="py-3.5 pr-4 text-slate-500 text-xs">{norm(s.email)}</td>
                      <td className="py-3.5 pr-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg border text-[11px] font-bold"
                          style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>
                          {norm(s.status)}
                        </span>
                      </td>
                      <td className="py-3.5 pr-4 text-slate-500 text-xs whitespace-nowrap">
                        {s.submitted_date ? fmtDate(s.submitted_date) : "-"}
                      </td>
                      <td className="py-3.5 text-slate-500 text-xs">{norm(s.recruiter || s.recruiter_name)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <span className="text-xs text-slate-400 font-medium">
            {!loading && !error && `${submissions.length} submission${submissions.length !== 1 ? "s" : ""}`}
          </span>
          <button onClick={onClose}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-100 transition-all">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Description Modal ─── */
function DescriptionModal({ job, onClose }) {
  const [copied, setCopied] = useState(false);
  const ref = React.useRef(null);
  const html = String(job?.job_description_html || "")
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  const hasDesc = Boolean(html.trim());

  const copyDesc = async () => {
    try {
      const plain = ref.current?.innerText?.trim() || html;
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([plain], { type: "text/plain" }),
            "text/html":  new Blob([html],  { type: "text/html"  }),
          }),
        ]);
      } else await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(2,6,23,0.6)", backdropFilter:"blur(8px)" }}
      onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{ background:"#fff", border:"1px solid #e2e8f0",
          boxShadow:"0 32px 80px -16px rgba(0,0,0,0.3)",
          animation:"modalIn 0.3s cubic-bezier(0.34,1.56,0.64,1) both" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: "linear-gradient(90deg,#0f172a,#1d4ed8,#7c3aed)" }} />
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background:"linear-gradient(135deg,#0f172a,#1e3a8a)" }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900 truncate max-w-[300px]">{job?.job_title}</h4>
              <p className="text-[11px] text-slate-400 uppercase tracking-widest font-medium mt-0.5">Job Description</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyDesc} disabled={!hasDesc}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all">
              {copied ? <><CheckCheck className="w-3.5 h-3.5 text-emerald-500" /> Copied</> : <><Download className="w-3.5 h-3.5" /> Copy</>}
            </button>
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-6 max-h-[60vh] overflow-auto">
          {hasDesc
            ? <div ref={ref} className="text-sm text-slate-700 leading-7 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
            : (
              <div className="flex flex-col items-center py-12 gap-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-300" />
                </div>
                <p className="text-sm text-slate-400">No description available.</p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sort Icon ─── */
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ArrowUpDown className="w-3 h-3 text-slate-300 ml-1 inline" />;
  return sortDir === "asc"
    ? <ChevronUp   className="w-3 h-3 text-blue-500 ml-1 inline" />
    : <ChevronDown className="w-3 h-3 text-blue-500 ml-1 inline" />;
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
const JobListings = ({ currentUser }) => {
  const [jobs, setJobs]                       = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [searchTerm, setSearchTerm]           = useState("");
  const [filterStatus, setFilterStatus]       = useState("all");
  const [filterClient, setFilterClient]       = useState([]);
  const [filterAddressed, setFilterAddressed] = useState("all");
  const [filterJobType, setFilterJobType]     = useState([]);
  const [filterCountry, setFilterCountry]     = useState([]);
  const [filterRemote, setFilterRemote]       = useState("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage]         = useState(1);
  const [serverTotal, setServerTotal]         = useState(0);
  const [totalPages, setTotalPages]           = useState(1);
  const [submissionsJob, setSubmissionsJob]   = useState(null);
  const [descriptionJob, setDescriptionJob]   = useState(null);
  const [showMoreActions, setShowMoreActions] = useState(null);
  const [expandedInterviews, setExpandedInterviews]   = useState(new Set());
  const [loadingInterviews, setLoadingInterviews]     = useState(new Set());

  const [showClientDropdown, setShowClientDropdown]   = useState(false);
  const [showJobTypeDropdown, setShowJobTypeDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown]   = useState(false);
  const [showAddressedDropdown, setShowAddressedDropdown] = useState(false);
  const [showRemoteDropdown, setShowRemoteDropdown]   = useState(false);

  const PER_PAGE      = 20;
  const currentUserId = String(currentUser?.id ?? currentUser?.user_id ?? "").trim();

  const mapJob = (j) => {
    const addressedNames = normOpt(j.addressed_by_names || j.addressed_user_names);
    const addressed = addressedNames.split(",").map(s => s.trim()).filter(Boolean);
    return {
      id:                          norm(j.job_pk),
      job_pk:                      norm(j.job_pk),
      external_id:                 norm(j.external_id),
      job_code:                    norm(j.job_code),
      job_title:                   norm(j.public_job_title || j.job_title, "Untitled"),
      job_description_html:        norm(j.job_description_html || j.job_description, ""),
      job_status:                  norm(j.job_status, "Active"),
      job_type:                    norm(j.job_type, "-"),
      client:                      norm(j.client, "-"),
      location:                    norm(j.location || [j.city, j.state].filter(Boolean).join(", "), "-"),
      country:                     norm(j.country, "-"),
      remote_job:                  norm(j.remote_job, "-"),
      addressed_status:            addressed.length > 0 ? "addressed" : "not_addressed",
      addressed_by_names:          addressedNames,
      current_user_addressed_flag: norm(j.current_user_addressed_flag, "0"),
      positions:                   Number(j.number_of_positions) || 0,
      start_date:                  norm(j.job_start_date || j.created_at, "-"),
    };
  };

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams();
      p.append("page",            String(currentPage));
      p.append("per_page",        String(PER_PAGE));
      p.append("current_user_id", currentUserId);
      if (searchTerm.trim())         p.append("q",          searchTerm.trim());
      if (filterStatus !== "all")    p.append("job_status", filterStatus);
      if (Array.isArray(filterClient)  && filterClient.length  > 0) p.append("client",   filterClient.join(","));
      if (Array.isArray(filterJobType) && filterJobType.length > 0) p.append("job_type", filterJobType.join(","));
      if (Array.isArray(filterCountry) && filterCountry.length > 0) p.append("country",  filterCountry.join(","));
      if (filterAddressed !== "all") p.append("is_addressed", filterAddressed);
      if (filterRemote    !== "all") p.append("remote_job",   filterRemote);

      const res = await fetch(`https://candi.vdpl.co/api/index.php/getActiveJobs?${p}`, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!(data?.success === 1 || data?.success === true))
        throw new Error(data?.message || "Failed to fetch jobs");

      const rows = Array.isArray(data?.data) ? data.data : [];
      setJobs(rows.map(mapJob));
      const total = Number(data?.pagination?.total) || rows.length;
      setServerTotal(total);
      setTotalPages(Number(data?.pagination?.total_pages) || Math.max(1, Math.ceil(total / PER_PAGE)));
    } catch (err) {
      console.error("JobListings fetch error:", err);
      setJobs([]); setServerTotal(0); setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterStatus, filterClient, filterAddressed,
      filterJobType, filterCountry, filterRemote, currentUserId]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { setCurrentPage(1); },
    [searchTerm, filterStatus, filterClient, filterAddressed, filterJobType, filterCountry, filterRemote]);

  const uniqueClients   = [...new Set(jobs.map(j => j.client).filter(v => v && v !== "-"))];
  const uniqueStatuses  = [...new Set(["Active","Open","Re-Open", ...jobs.map(j => j.job_status)])];
  const uniqueTypes     = [...new Set(jobs.map(j => j.job_type).filter(v => v && v !== "-"))];
  const uniqueCountries = [...new Set(jobs.map(j => j.country).filter(v => v && v !== "-"))];
  const uniqueRemotes   = [...new Set(jobs.map(j => j.remote_job).filter(v => v && v !== "-"))];

  const clearFilters = () => {
    setSearchTerm(""); setFilterStatus("all"); setFilterClient([]);
    setFilterAddressed("all"); setFilterJobType([]); setFilterCountry([]); setFilterRemote("all");
  };

  const toggleFilter = (setFilter, value) => {
    setFilter(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(value) ? current.filter(item => item !== value) : [...current, value];
    });
  };

  const pageStart      = serverTotal === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const pageEnd        = Math.min(currentPage * PER_PAGE, serverTotal);
  const addressedCount = jobs.filter(j => j.addressed_status === "addressed").length;
  const openCount      = jobs.filter(j => j.job_status === "Open").length;
  const paginatedJobs  = jobs;

  /* ── Single-select dropdown ── */
  const SingleSelectDropdown = ({ label, value, onChange, options, showDropdown, setShowDropdown, placeholder = "Select..." }) => {
    const displayText = value === 'all' ? placeholder : value;
    return (
      <div className="relative" style={{ zIndex: showDropdown ? 200 : 'auto' }}>
        {label && (
          <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-blue-500"></span>{label}
          </label>
        )}
        <button type="button" onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white hover:border-slate-300 transition-colors text-left flex items-center justify-between flex-shrink-0">
          <span className={value === 'all' ? 'text-slate-400' : 'text-slate-900'}>{displayText}</span>
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        {showDropdown && (
          <>
            <div className="fixed inset-0 z-[150]" onClick={() => setShowDropdown(false)} />
            <div className="absolute z-[200] mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-2xl animate-slideDown"
              style={{ minWidth: '200px' }}>
              <div className="p-2">
                {options.map((option) => (
                  <button key={option.value} onClick={() => { onChange(option.value); setShowDropdown(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 rounded-md text-xs transition-colors ${
                      value === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'
                    }`}>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  /* ── Multi-select dropdown ── */
  const MultiSelectDropdown = ({ label, options, selectedValues, onToggle, showDropdown, setShowDropdown, placeholder = "Select..." }) => {
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];
    const displayText = selectedArray.length === 0 ? placeholder
      : selectedArray.length === 1 ? selectedArray[0]
      : `${selectedArray.length} selected`;
    return (
      <div className="relative" style={{ zIndex: showDropdown ? 200 : 'auto' }}>
        {label && (
          <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-blue-500"></span>{label}
          </label>
        )}
        <button type="button" onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white hover:border-slate-300 transition-colors text-left flex items-center justify-between flex-shrink-0">
          <span className={selectedArray.length === 0 ? 'text-slate-400' : 'text-slate-900'}>{displayText}</span>
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        {showDropdown && (
          <>
            <div className="fixed inset-0 z-[150]" onClick={() => setShowDropdown(false)} />
            <div className="absolute z-[200] mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-2xl animate-slideDown"
              style={{ minWidth: '200px' }}>
              <div className="p-2">
                {options.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500 text-center">No options available</div>
                ) : (
                  options.map((option) => (
                    <label key={option} className="flex items-center px-3 py-2 hover:bg-slate-50 rounded-md cursor-pointer group">
                      <input type="checkbox" checked={selectedArray.includes(option)} onChange={() => onToggle(option)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                      <span className="ml-2 text-xs text-slate-700 group-hover:text-slate-900">{option}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedArray.length > 0 && (
                <div className="border-t border-slate-200 p-2">
                  <button type="button" onClick={() => { onToggle(null, true); setShowDropdown(false); }}
                    className="w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors">
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.96) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes shimPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        .animate-slideDown { animation: slideDown 0.2s ease-out; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.5); }
        select { -webkit-appearance: none; appearance: none; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .row-hover:hover { background: #f8fafc !important; }
        .sort-th { cursor: pointer; user-select: none; }
        .sort-th:hover { color: #1d4ed8 !important; }
        .prose p { margin: 0.5em 0; }
        .prose ul { padding-left: 1.25em; }
        .prose li { margin: 0.25em 0; }
      `}</style>

      <div className="space-y-2 px-2 sm:px-4 lg:px-0">

        {/* Sticky Top Bar */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm -mx-2 sm:-mx-4 lg:mx-0 px-2 sm:px-4 lg:px-2 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 tracking-tight">Job Listings</h3>
                <p className="text-xs text-slate-500">{serverTotal} total</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                <Target className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-semibold text-blue-700">{openCount} Open</span>
              </div>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                <span className="text-xs font-semibold text-green-700">{addressedCount} Addressed</span>
              </div>
              <button onClick={fetchJobs} disabled={loading}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" title="Refresh data">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Filter Container */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" style={{ overflow: 'visible' }}>
          {/* Search */}
          <div className="p-4 border-b border-slate-100">
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors">
                <Search className="w-5 h-5" />
              </div>
              <input type="text" placeholder="Search jobs by title, client, job code..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-slate-50 hover:bg-white transition-all placeholder:text-slate-400" />
            </div>
          </div>

          {/* Quick Filters */}
          <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100" style={{ overflow: 'visible' }}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Quick Filters:</span>
              <div style={{ minWidth: '140px' }}>
                <SingleSelectDropdown value={filterStatus} onChange={setFilterStatus}
                  options={[{ value: 'all', label: 'All Status' }, ...uniqueStatuses.filter(s => s !== 'all').map(s => ({ value: s, label: s }))]}
                  showDropdown={showStatusDropdown} setShowDropdown={setShowStatusDropdown} placeholder="Status" />
              </div>
              <div style={{ minWidth: '140px' }}>
                <MultiSelectDropdown options={uniqueClients} selectedValues={filterClient}
                  onToggle={(value, clearAll) => { if (clearAll) setFilterClient([]); else toggleFilter(setFilterClient, value); }}
                  showDropdown={showClientDropdown} setShowDropdown={setShowClientDropdown} placeholder="Clients" />
              </div>
              <div style={{ minWidth: '140px' }}>
                <MultiSelectDropdown options={uniqueTypes} selectedValues={filterJobType}
                  onToggle={(value, clearAll) => { if (clearAll) setFilterJobType([]); else toggleFilter(setFilterJobType, value); }}
                  showDropdown={showJobTypeDropdown} setShowDropdown={setShowJobTypeDropdown} placeholder="Job Types" />
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                    showAdvancedFilters
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30'
                      : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}>
                  <Filter className="w-4 h-4" />
                  <span>Advanced</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Active Filter Pills */}
          {(searchTerm || filterStatus !== 'all' ||
            (Array.isArray(filterClient)  && filterClient.length  > 0) ||
            (Array.isArray(filterJobType) && filterJobType.length > 0) ||
            (Array.isArray(filterCountry) && filterCountry.length > 0) ||
            filterAddressed !== 'all' || filterRemote !== 'all') && (
            <div className="px-4 py-3 bg-blue-50/30 border-b border-blue-100/50">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-slate-600 mr-1">Active:</span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-medium shadow-sm">
                    <Search className="w-3 h-3" />
                    <span className="truncate max-w-[120px]">"{searchTerm}"</span>
                    <button onClick={() => setSearchTerm('')}><X className="w-3.5 h-3.5" /></button>
                  </span>
                )}
                {filterStatus !== 'all' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-200 text-green-700 rounded-lg text-xs font-medium shadow-sm">
                    <CircleDot className="w-3 h-3" />{filterStatus}
                    <button onClick={() => setFilterStatus('all')}><X className="w-3.5 h-3.5" /></button>
                  </span>
                )}
                {Array.isArray(filterClient) && filterClient.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-purple-200 text-purple-700 rounded-lg text-xs font-medium shadow-sm">
                    <Building2 className="w-3 h-3" />{filterClient.length} Client{filterClient.length > 1 ? 's' : ''}
                    <button onClick={() => setFilterClient([])}><X className="w-3.5 h-3.5" /></button>
                  </span>
                )}
                {Array.isArray(filterJobType) && filterJobType.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-xs font-medium shadow-sm">
                    <Briefcase className="w-3 h-3" />{filterJobType.length} Type{filterJobType.length > 1 ? 's' : ''}
                    <button onClick={() => setFilterJobType([])}><X className="w-3.5 h-3.5" /></button>
                  </span>
                )}
                {Array.isArray(filterCountry) && filterCountry.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-teal-200 text-teal-700 rounded-lg text-xs font-medium shadow-sm">
                    <MapPin className="w-3 h-3" />{filterCountry.length} Countr{filterCountry.length > 1 ? 'ies' : 'y'}
                    <button onClick={() => setFilterCountry([])}><X className="w-3.5 h-3.5" /></button>
                  </span>
                )}
                <button onClick={clearFilters}
                  className="ml-auto text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors">
                  <X className="w-3.5 h-3.5" /> Clear All
                </button>
              </div>
            </div>
          )}

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="border-t border-slate-200 bg-slate-50/30 overflow-visible">
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
                <MultiSelectDropdown label="Country" options={uniqueCountries} selectedValues={filterCountry}
                  onToggle={(value, clearAll) => { if (clearAll) setFilterCountry([]); else toggleFilter(setFilterCountry, value); }}
                  showDropdown={showCountryDropdown} setShowDropdown={setShowCountryDropdown} placeholder="All" />
                <SingleSelectDropdown label="Addressed" value={filterAddressed} onChange={setFilterAddressed}
                  options={[{ value: 'all', label: 'All' }, { value: '1', label: 'Addressed' }, { value: '0', label: 'Not Addressed' }]}
                  showDropdown={showAddressedDropdown} setShowDropdown={setShowAddressedDropdown} placeholder="All" />
                <SingleSelectDropdown label="Remote" value={filterRemote} onChange={setFilterRemote}
                  options={[{ value: 'all', label: 'All' }, ...uniqueRemotes.map(r => ({ value: r, label: r }))]}
                  showDropdown={showRemoteDropdown} setShowDropdown={setShowRemoteDropdown} placeholder="All" />
              </div>
              <div className="px-3 pb-2 flex items-center justify-between">
                <span className="text-[10px] text-slate-600">
                  <span className="font-semibold text-blue-600">{jobs.length}</span> matches
                </span>
                <button onClick={clearFilters}
                  className="px-2 py-1 text-[10px] text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors">
                  Reset all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Jobs List */}
        <div className="space-y-3 sm:space-y-4">
          {loading ? (
            <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 text-center border border-slate-200">
              <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-slate-400 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-slate-500">Loading jobs...</p>
            </div>
          ) : paginatedJobs.length === 0 ? (
            <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 text-center border border-slate-200">
              <Briefcase className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">No jobs found</h3>
              <p className="text-sm sm:text-base text-slate-500 mb-6">
                {jobs.length === 0
                  ? 'No jobs have been added yet.'
                  : 'No jobs match your current search and filter criteria. Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            paginatedJobs.map((job) => {
              const stage      = getStage(job.job_status);
              const StageIcon  = stage.icon;
              const isAddressed = job.addressed_status === "addressed";

              return (
                <div key={job.id} className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-sm border bg-white border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all duration-200">
                  <div className="p-4 sm:p-6">

                    {/* Header Row */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex flex-wrap items-center gap-2 flex-1">
                            <h3 className="text-lg sm:text-xl font-bold text-slate-900 break-words">{job.job_title}</h3>
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border"
                              style={{ background: stage.bg, color: stage.color, borderColor: stage.border }}>
                              <StageIcon className="w-3 h-3" />{job.job_status}
                            </span>
                            {isAddressed && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700">
                                <CheckCircle className="w-3 h-3" /> Addressed
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="font-medium text-sm sm:text-base text-slate-600 break-words">{job.job_code}</p>
                        {job.positions > 0 && (
                          <p className="text-xs sm:text-sm text-slate-500">{job.positions} position{job.positions !== 1 ? 's' : ''}</p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <button
                          disabled={loadingInterviews.has(job.id)}
                          onClick={() => {
                            if (loadingInterviews.has(job.id)) return;
                            setExpandedInterviews(prev => {
                              const next = new Set(prev);
                              next.has(job.id) ? next.delete(job.id) : next.add(job.id);
                              return next;
                            });
                          }}
                          className={`flex items-center px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold transition-all duration-300 rounded-xl ${
                            loadingInterviews.has(job.id)
                              ? 'text-white bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-500 shadow-lg shadow-purple-500/20 opacity-70 cursor-not-allowed'
                              : expandedInterviews.has(job.id)
                              ? 'text-white bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-700 shadow-lg shadow-purple-500/30'
                              : 'text-white bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-600 hover:from-purple-600 hover:via-indigo-600 hover:to-blue-700 shadow-lg shadow-purple-500/30 hover:shadow-xl hover:scale-105'
                          }`}
                        >
                          {loadingInterviews.has(job.id)
                            ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /><span>Loading...</span></>
                            : <><Eye className="w-4 h-4 mr-2" /><span>{expandedInterviews.has(job.id) ? 'Hide' : 'View'} Interviews</span>
                               <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${expandedInterviews.has(job.id) ? 'rotate-180' : ''}`} /></>
                          }
                        </button>

                        <div className="relative z-20">
                          <button onClick={() => setShowMoreActions(showMoreActions === job.id ? null : job.id)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors relative z-20">
                            <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                          {showMoreActions === job.id && (
                            <div className="absolute right-0 top-full mt-2 w-48 sm:w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-30">
                              <div className="py-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDescriptionJob(job); setShowMoreActions(null); }}
                                  className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                                  View Description
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</p>
                        <p className="text-sm font-bold text-slate-900 break-words flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />{job.client}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-blue-600" />Location
                        </p>
                        <p className="text-sm text-slate-900 break-words">{job.location}</p>
                        {job.country !== '-' && <p className="text-xs text-slate-500">{job.country}</p>}
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Job Type</p>
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 rounded-lg px-2.5 py-1 inline-block">{job.job_type}</span>
                        {job.remote_job !== '-' && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Wifi className="w-3 h-3" />{job.remote_job}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tentative Start Date</p>
                        <p className="text-sm text-slate-900">{job.start_date !== '-' ? fmtDate(job.start_date) : '-'}</p>
                        {isAddressed && job.addressed_by_names && (
                          <p className="text-xs text-emerald-600 font-medium truncate">By: {job.addressed_by_names}</p>
                        )}
                      </div>
                    </div>

                    {/* Interviews Section */}
                    {expandedInterviews.has(job.id) && (
                      <InterviewsSection
                        job={job}
                        currentUser={currentUser}
                        onLoadingChange={(jobId, isLoading) => {
                          setLoadingInterviews(prev => {
                            const next = new Set(prev);
                            isLoading ? next.add(jobId) : next.delete(jobId);
                            return next;
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
              <div className="text-xs sm:text-sm text-slate-700 text-center sm:text-left">
                Showing <span className="font-semibold">{pageStart}</span> to <span className="font-semibold">{pageEnd}</span> of <span className="font-semibold">{serverTotal}</span> jobs
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                  className="p-2 sm:p-3 border border-slate-300 rounded-lg sm:rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                      className={`px-2.5 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl transition-colors ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                          : 'text-slate-700 hover:bg-slate-50 border border-slate-300'
                      }`}>
                      {pageNum}
                    </button>
                  );
                })}
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
                  className="p-2 sm:p-3 border border-slate-300 rounded-lg sm:rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODALS */}
      {submissionsJob && <SubmissionsModal job={submissionsJob} onClose={() => setSubmissionsJob(null)} />}
      {descriptionJob && <DescriptionModal job={descriptionJob} onClose={() => setDescriptionJob(null)} />}
    </>
  );
};

export default JobListings;